import { TASK_GRID_PROPS, TASK_GRID_STATE } from "./Types";
import React, { ReactElement } from "react";
import {
  HBox,
  PuzzleButton,
  PuzzleDropDownButton,
  WithTooltip, withDynamicSize
} from "puzzle";
import gridFilter from "../../Abstract/GridFilter";
import Grid from "../../Abstract/Grid/Grid";
import { GridCellProps } from "@progress/kendo-react-grid";
import {
  commonLocalizationGetter, disableActions,
  getTableTdAccessibilityProps, isActionAvailable,
  isDateExpired,
  isSubmissionDueSoon,
  popupAlignment,
  SUPPORTED_EDITORS
} from "../../util/Util";
import classNames from "classnames";
import GridProgressBar from "../../util/GridProgressBar";
import ActionExecutorInstance from "../TaskList/ActionExecutor/ActionExecutor";
import _ from "lodash";
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import Policies from "../../util/Policies";
import WordCountLink from "../../util/WordCountLink/WordCountLink";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";

export class TaskGrid extends Grid<TASK_GRID_PROPS, TASK_GRID_STATE> {
  namespacePrefix = 'TaskGrid';
  static defaultProps = {
    ...Grid.defaultProps,
    nonReordeableColumns: ['checkboxSelection', 'statusColor', 'isFavorite', 'menuAction'],
    nonResizableColumns: ['checkboxSelection', 'statusColor', 'isFavorite', 'fileProgress', 'menuAction', 'instructions', 'notes'],
    defaultSort: [{
      field: 'submissionId',
      dir: 'desc'
    }],
    actionsNames: {
      filter: 'filterMainGrid',
      refreshGrid: 'refreshMainGridWhenDrawerIsNotOpen',
      refreshGridOnCurrentPosition: 'refreshApp' //todo consider adding refresh for saved searches also
    },
    moduleName: 'MainGrid', //todo change to AbstractGrid, also change on MainGrid.tsx class
    getDataUrl: '/jobPhaseView',
    uniqueField: 'taskId'
  };
  menuActionWidth = '130px';

  /**
   * @inheritDoc
   */
  additionalGridTitle = () => {
    let { checkboxSelectionData, warningBannerMessage } = this.state;
    let total = this.gridRef?.gridRef?.state.data?.length || 0;
    return (
      <>
        <HBox alignItemsBaseLine gap={2}>
          <h2 className={'tp-font-size-xl tp-text-semibold'}>{this.translate('taskList')}</h2>
          <span className={'pd-tasks-counter tp-font-size-sm'}>
          <span className={'pd-task-number'}>
            {this.translate('tasksCount', { total: total })}
          </span>
            {this.checkboxSelectionEnabled && <>
              <span className='tp-space-right-half-1 tp-space-left-half-1'>|</span>
              <span className={'pd-task-selection tp-color-light-LM'}>
                {this.translate('tasksCheckboxSelected', { count: checkboxSelectionData?.length })}
              </span>
            </>}
          </span>
        </HBox>
        <div role={'alert'} className={'pd-notification-alert'}>
          <NotificationGroup>
            {warningBannerMessage && <Notification
              className={'tp-informational'}
              type={{ style: 'warning', icon: true }}
            >
              {warningBannerMessage}
            </Notification>}
          </NotificationGroup>
        </div>
      </>
    )
  }

  /**
   * @inheritDoc
   */
  isStatusColorNeeded = (dataItem: any) => {
    // const { CANCELLED, COMPLETED, ON_HOLD } = SUBMISSION_STATUSES_MAP_SERVER_CORE_UI;
    // const statusName = dataItem?.taskStatus?.name;
    // return !(CANCELLED.includes(statusName) || COMPLETED.includes(statusName) || (ON_HOLD.includes(statusName) && isDateExpired(dataItem.phaseDueDate)));
    const isExpired = isDateExpired(dataItem.phaseDueDate);
    return isExpired;
  };

  /**
   * Cell renderer for phaseDueDate column
   */
  phaseDueDateRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field: string = cellProps.field || '';
    const dueDate = dataItem[field];
    let className = cellProps.className;
    const colorNeeded = this.isStatusColorNeeded(dataItem);
    if (colorNeeded && dueDate) {
      const { dueSoon } = this.state;
      const isExpired = isDateExpired(dueDate);
      const isDueSoon = isSubmissionDueSoon(dueDate, dueSoon);
      className = classNames(cellProps.className, {
        'tp-date-due-soon': isDueSoon,
        'tp-date-expired': isExpired
      });
    }
    let date;
    if (dueDate) {
      date = new Date(dueDate);
    } else {
      date = '';
    }
    let loadingCell = this.loadingCell(cellProps);

    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (<td
        className={className}
        {...tdProps}
      >
        {this.translateByNamespace('date.dateWithTime', { date: date })}
      </td>);
    }
  };

  /**
   * Cell renderer for phaseStartDate column
   */
  phaseStartDateRenderer = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field: string = cellProps.field || '';
    const startDate = dataItem[field];
    let date;
    if (startDate) {
      date = new Date(startDate);
    } else {
      date = '';
    }
    let loadingCell = this.loadingCell(cellProps);
    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td
          className={cellProps.className}
          {...tdProps}
        >
          {this.translateByNamespace('date.dateWithTime', { date: date })}
        </td>
      );
    }
  }

  /**
   * Returns either start or deliver icon with appropriate action on it
   */
  getAppropriateAction = (cellProps: GridCellProps) => {
    const {
      className,
      dataItem = {},
      style
    } = cellProps;
    let { checkboxSelectionData } = this.state;
    // @ts-ignore
    let checkboxSelection = dataItem.checkboxSelection && checkboxSelectionData.length > 1;
    let children = null;
    let decodedTasks: string[] = [];
    if (dataItem.availableTasks) {
      decodedTasks = disableActions([dataItem]);
    }
    const statusName = _.get(dataItem, 'taskStatus.name');
    const allParsable = _.get(dataItem, 'parsableState') === 'ALL_PARSABLE';
    let externalEditors = _.get(dataItem, 'availableEditors') || [];
    const openDownloadEnabled = _.get(dataItem, 'openDownloadEnabled');
    const isSideBySideEditor = !!externalEditors.length && externalEditors[0] === "SIDE_BY_SIDE";
    externalEditors = externalEditors.filter((item: string) => _.includes(SUPPORTED_EDITORS, item));
    const numberOfEditors = externalEditors?.length;

    let openButtonProps = {
      'aria-label': this.translate('open'),
      disabled: checkboxSelection,
      fillMode: "solid",
      themeColor: 'primary',
      titleText: this.translate('open'),
      tooltipPosition: 'left',
      withTooltip: true
    };

    if (statusName === 'AVAILABLE' && decodedTasks.includes('claim job phase')) {
      children = <PuzzleButton
        aria-label={this.translate('claim')}
        disabled={checkboxSelection}
        titleText={this.translate('claim')}
        fillMode="solid"
        themeColor={'dark'}
        onClick={() => {
          this.dispatch('claim_job_phase', '', { submissionData: dataItem });
        }}
        tooltipPosition={'left'}
        withTooltip
      >
        {this.translate('claim')}
      </PuzzleButton>
    } else if (statusName === 'AVAILABLE' && decodedTasks.includes('reserve job phase')) {
      children = <PuzzleButton
        aria-label={this.translate('reserve')}
        className={'tp-additional-button'}
        disabled={checkboxSelection}
        titleText={this.translate('reserve')}
        fillMode="solid"
        themeColor={'dark'}
        onClick={() => {
          this.dispatch('reserve_job_phase', '', { submissionData: dataItem });
        }}
        tooltipPosition={'left'}
        withTooltip
      >
        {this.translate('reserve')}
      </PuzzleButton>
    } else if (decodedTasks.includes('complete jobPhase taskGrid action')) {
      const completeText = this.translate('complete');
      children = <PuzzleButton
        aria-label={completeText}
        disabled={checkboxSelection}
        fillMode={'solid'}
        themeColor={'success'}
        onClick={() => {
          this.dispatch('completeConfirmationWarning', '', {
            submissionData: dataItem
          });
        }}
        titleText={completeText}
        tooltipPosition={'left'}
        withTooltip={true}
      >
        {completeText}
      </PuzzleButton>
    } else if (_.includes(['READY', 'RETURNED', 'IN_PROGRESS'], statusName)) {
      if (allParsable) {
        if (numberOfEditors === 1) {
          children = <PuzzleButton
            {...openButtonProps}
            onClick={() => {
              ActionExecutorInstance.executeOpenTask(dataItem, externalEditors[0]);
            }}
          >
            {this.translate('open')}
          </PuzzleButton>
        } else if (numberOfEditors > 1) {
          children = <PuzzleDropDownButton
            aria-label={this.translate('open')}
            disabled={checkboxSelection}
            icon={'arrow-s'}
            items={this.renderMultipleEditors(externalEditors)}
            onItemClick={this.onMultiItemOpenClick.bind(this, dataItem)}
            popupSettings={popupAlignment('')}
            reverse
            text={this.translate('open')}
            themeColor={'primary'}
          />
        } else if (!numberOfEditors) {
          children = <PuzzleButton
            icon={'tp-icon tp-download-thin'}
            reverse
            onClick={() => {
              let paramsGLPFiles = { downloadType: 'WF5' };
              this.dispatch('downloadFiles', '', { submissionData: dataItem, params: paramsGLPFiles });
            }}
            {...openButtonProps}
            disabled={!openDownloadEnabled}
          >
            {this.translate('open')}
          </PuzzleButton>
        }
      } else {
        children = <PuzzleButton
          icon={'tp-icon tp-download-thin'}
          reverse
          onClick={() => {
            let paramsGLPFiles = { downloadType: 'WF5' };
            this.dispatch('downloadFiles', '', { submissionData: dataItem, params: paramsGLPFiles });
          }}
          {...openButtonProps}
          disabled={!openDownloadEnabled}
        >
          {this.translate('open')}
        </PuzzleButton>

        if (isSideBySideEditor) {
          children = null;
        }
      }
    } else if (statusName === 'RESERVED' && !isSideBySideEditor) {
      let aditionalProps = {};
      if (!numberOfEditors || !allParsable) {
        aditionalProps = {
          icon: 'tp-icon tp-download-thin',
          reverse: true
        }
      }

      children = <PuzzleButton
        {...openButtonProps}
        {...aditionalProps}
        disabled={true}
      >
        {this.translate('open')}
      </PuzzleButton>

    }

    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td
        {...tdProps}
        aria-label={this.translate('columnHeaders.submissionAction')}
        className={className}
        style={style}
      >
        {children}
      </td>
    );
  };

  /**
   * Renders editorNames for Open bttn
   * @param editors
   */
  renderMultipleEditors = (editors: string[]) => {
    return editors.map((editor) => {
      return {
        editor: editor,
        text: this.translate(`allFiles`, { editorName: this.translate(`editorNames.${editor}`) })
      }
    });
  };

  /**
   * Handler for dropdown items click.
   * @param dataItem
   * @param element
   */
  onMultiItemOpenClick = (dataItem: any, element: DropDownButtonItemClickEvent): void => {
    const {
      editor
    } = element.item;

    ActionExecutorInstance.executeOpenTask(dataItem, editor);
  };

  /**
   * @inheritDoc
   */
  getFolderParamForRequest = () => 'JOB_PHASE';

  /**
   * Action menu column renderer
   */
  menuActionColumnCellRenderer = (cellProps: GridCellProps) => {
    let menuActionCell = this.loadingCell(cellProps);
    if (!menuActionCell) {
      menuActionCell = this.getAppropriateAction(cellProps);
    }
    return menuActionCell
  };

  /**
   * Cell render for Offer status column
   */
  budgetStatusRender = (cellProps: GridCellProps) => {
    let budgetStatusClass = '';
    let budgetStatusText = '';
    const { dataItem } = cellProps;
    const { budgetStatus } = dataItem;
    let hasViewOfferTask = isActionAvailable(dataItem, 'job phase view offer');

    if (budgetStatus) {
      budgetStatusClass = classNames(`pd-offer-status-cell tp-text-semibold ${hasViewOfferTask ? 'tp-pointable' : ''} ${budgetStatus.toLowerCase()}`);
      budgetStatusText = commonLocalizationGetter(`BUDGET.statuses.${budgetStatus}`);
    }
    let loadingCell = this.loadingCell(cellProps);

    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td {...tdProps}>

          <HBox
            alignItemsCenter
            className={budgetStatusClass}
            gap={1}
            htmlTag={'span'}
            {...(hasViewOfferTask && { onClick: () => this.dispatch('job_phase_view_offer', '', { submissionData: dataItem }) })}
          >
            {budgetStatusText}
          </HBox>
        </td>
      )
    }
  };

  /**
   * Progress column cell renderer
   */
  progressCellRender = (cellProps: GridCellProps) => {
    let completedProgress: number = 0;
    let progress: number = 0;
    let dataItem = cellProps?.dataItem;
    let errorBar = false;
    if (cellProps?.field) {
      completedProgress = dataItem[cellProps.field]
      progress = ['AVAILABLE', 'NOT_CLAIMED'].includes(dataItem.taskStatus?.name) ? 0 : 100
    }

    let loadingCell = this.loadingCell(cellProps);
    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td {...tdProps} className={cellProps.className}>
          <GridProgressBar completedProgress={completedProgress} progress={progress} errorBar={errorBar} />
        </td>
      )
    }
  };

  /**
   * TaskStatus color cell render
   */
  statusColorCellRender = (cellProps: GridCellProps): ReactElement<HTMLTableCellElement> => {
    const dataItem = cellProps.dataItem;
    const { phaseDueDate } = dataItem;
    let className = `${cellProps.className} pd-status-color-column-cell`;
    let title = '';
    if (phaseDueDate) {
      const { dueSoon } = this.state;
      const isExpired = isDateExpired(phaseDueDate);
      const isDueSoon = isSubmissionDueSoon(phaseDueDate, dueSoon);
      className = classNames(`${cellProps.className} pd-status-color-column-cell`, {
        'tp-status-warning': isDueSoon,
        'tp-status-expired': isExpired
      });
      title = this.generateDueDateTooltipTitle(isExpired, isDueSoon, phaseDueDate);
    }

    let tdProps: any = getTableTdAccessibilityProps(cellProps);
    tdProps.className = className;
    return title.length ?
      <WithTooltip context={this} htmlTag='td' className={className} title={title} additionalProps={tdProps}>
        <div title={title} className='pd-status-color-column-indicator' />
      </WithTooltip> :
      <td {...tdProps} className={className}>
        <div className='pd-status-color-column-indicator' />
      </td>;
  };

  /**
   * Cell render for status column
   */
  taskStatusRender = (cellProps: GridCellProps) => {
    let statusClass = '';
    let statusText = '';
    const { taskStatus } = cellProps.dataItem;
    if (taskStatus) {
      let statusName = taskStatus.name;
      statusClass = classNames(`tp-main-grid-status-cell ${statusName.replace(' ', '-').toLowerCase()}`);
      statusText = commonLocalizationGetter(`TASK.statuses.${statusName}`)
    }

    let loadingCell = this.loadingCell(cellProps);


    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td {...tdProps} className={cellProps.className}>
          <div className={statusClass}>
            {statusText}
          </div>
        </td>
      )
    }
  };

  /**
   * Default cell renderer
   */
  defaultRenderer = ((cellProps: GridCellProps) => {
    const { dataItem, field } = cellProps;
    let valueItem;
    if (field) {
      let properties = field.split('.');
      if (properties.length === 1) {
        valueItem = dataItem[properties[0]];
      } else {
        valueItem = dataItem[properties[0]] && dataItem[properties[0]][properties[1]];
      }
    }

    let loadingCell = this.loadingCell(cellProps);

    if (loadingCell) {
      return loadingCell;
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td className={cellProps.className} {...tdProps}>
          {valueItem}
        </td>
      )
    }
  });

  /**
   * Words column cell renderer
   */
  wordsCellRender = (cellProps: GridCellProps) => {
    let loadingCell = this.loadingCell(cellProps);

    if (loadingCell) {
      return loadingCell
    } else {
      const tdProps = getTableTdAccessibilityProps(cellProps);
      return (
        <td {...tdProps} className={`${cellProps.className} pd-cell-with-small-h-padding`}>
          <WordCountLink
            beforeAction={this.selectSubmission.bind(this, cellProps.dataItem)}
            data={{
              submissionName: cellProps.dataItem.submissionName,
              submissionId: cellProps.dataItem.submissionId,
              taskId: cellProps.dataItem.taskId,
              ...cellProps.dataItem, isSupplierView: true
            }}
            onlyNumber={true}
            selectSubmission={true}
          />
        </td>
      )
    }
  };

  /**
   * Drop event handler handler
   * @param e
   */
  onDropHandler = (e: React.DragEvent) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    this.setState({
      dragging: false
    }, () => {
      this.dispatch('job_phase_upload', '', {
        files
      });
    });
  };

  /**
   * @override
   */
  renderRefreshButton() {
    let render = null;
    let hasDeactivateRefreshButton = Policies.hasDeactivateRefreshButton;
    if (!hasDeactivateRefreshButton) {
      render = super.renderRefreshButton();
    }
    return render as JSX.Element;
  }

  /**
   * @inheritDoc
   */
  showDragAndDrop = () => {
    let {
      dragging,
      drawerOpen
    } = this.state;

    return dragging && !drawerOpen;
  }

  /**
   * @inheritDoc
   */
  CELL_RENDER_MAP: any = {
    assignedTo: this.ownerAndAssignToCellRender,
    batchName: this.defaultRenderer,
    checkboxSelection: this.checkboxSelectionCellRenderer,
    customField: this.customFieldRender.bind(this),
    instructions: this.renderNotesOrInstructionIcon.bind(this, false),
    notes: this.renderNotesOrInstructionIcon.bind(this, true),
    menuAction: this.menuActionColumnCellRenderer.bind(this),
    phaseName: this.defaultRenderer,
    phaseDueDate: this.phaseDueDateRenderer,
    phaseStartDate: this.phaseStartDateRenderer,
    priority: this.priorityRenderer,
    fileCount: this.defaultRenderer,
    fileProgress: this.progressCellRender,
    budgetStatus: this.budgetStatusRender.bind(this),
    sourceLanguage: this.defaultRenderer,
    statusColor: this.statusColorCellRender.bind(this),
    submissionId: this.defaultRenderer,
    submissionName: this.defaultRenderer,
    targetLanguage: this.defaultRenderer,
    taskStatus: this.taskStatusRender.bind(this),
    wordCount: this.wordsCellRender
  };

  /**
   * @inheritDoc
   */
  GET_DATAINDEX_MAP: any = {
    assignedToRemoteCombo: "assignedTo",
    batchNameInputText: "batchName",
    pastDueDateComboBox: "phaseDueDate",
    phasesRemoteCombo: "phaseName",
    priorityList: 'priority',
    sourceLanguageValues: "sourceLanguage",
    submissionId: "submissionId",
    targetLanguageValues: "targetLanguage",
    taskStatusValues: "taskStatus",
    vendorBudgetStatuses: "budgetStatus"
  };
  /**
   * @inheritDoc
   */
  GET_FILTERNAME_MAP = {
    assignedTo: "assignedToRemoteCombo",
    batchName: "batchNameInputText",
    budgetStatus: "vendorBudgetStatuses",
    phaseDueDate: "pastDueDateComboBox",
    phaseName: "phasesRemoteCombo",
    priority: 'priority',
    sourceLanguage: "sourceLanguageValues",
    submissionId: "submissionId",
    targetLanguage: "targetLanguageValues",
    taskStatus: "taskStatusValues"
  };
}

export default withDynamicSize(gridFilter(TaskGrid));
